import Link from "next/link";
import { Balancer } from "react-wrap-balancer";
import { scrollToElement } from "@/utils/scrollToElement";

type CloudVmDataType = {
  heading: string;
  data: {
    cpu: string;
    memory: string;
    storage: string;
    bandwidth: string;
    monthlyPrice: number;
  }[];
  footer?: {
    btnText?: string;
    text1?: string | React.ReactNode;
  };
};

export const CLOUD_VM_INTEL_DEDICATED: CloudVmDataType = {
  heading: "Cloud VM - Intel Based Dedicated CPU (General Purpose) ",
  data: [
    {
      cpu: "2",
      memory: "4 GB",
      storage: "80 GB",
      bandwidth: "10 TB",
      monthlyPrice: 25.0,
    },
    {
      cpu: "4",
      memory: "8 GB",
      storage: "160 GB",
      bandwidth: "10 TB",
      monthlyPrice: 50.0,
    },
    {
      cpu: "8",
      memory: "16 GB",
      storage: "320 GB",
      bandwidth: "10 TB",
      monthlyPrice: 100.0,
    },
    {
      cpu: "16",
      memory: "32 GB",
      storage: "640 GB",
      bandwidth: "10 TB",
      monthlyPrice: 200.0,
    },
    {
      cpu: "32",
      memory: "64 GB",
      storage: "1280 GB",
      bandwidth: "10 TB",
      monthlyPrice: 400.0,
    },
    {
      cpu: "48",
      memory: "96 GB",
      storage: "1920 GB",
      bandwidth: "10 TB",
      monthlyPrice: 600.0,
    },
  ],
  footer: {
    text1: (
      <>
        Note : Intel Compute is based on Intel Scalable Architecture.
        <br />
        <Balancer>
          We offer a wide range of Cloud VM configurations to suit your workloads. The
          prices listed are indicative and may fluctuate based on resource availability
          and billing cycles. Reach out to our{" "}
          <Link
            href="#contact-us"
            onClick={(e) => {
              e.preventDefault();
              scrollToElement("contact-us", 120);
            }}
            target="_blank"
            style={{ textDecoration: "underline", color: "#1E87F0", fontWeight: 700 }}
          >
            sales team
          </Link>{" "}
          for customized plans or volume monthlyPrice.
        </Balancer>
      </>
    ),
    btnText: "Get a Custom Quote.",
  },
};

export const CLOUD_VM_AMD_DEDICATED: CloudVmDataType = {
  heading: "Cloud VM  - AMD Based Dedicated CPU (General Purpose)",
  data: [
    {
      cpu: "1",
      memory: "4 GB",
      storage: "30 GB",
      bandwidth: "10 TB",
      monthlyPrice: 30.0,
    },
    {
      cpu: "2",
      memory: "8 GB",
      storage: "50 GB",
      bandwidth: "10 TB",
      monthlyPrice: 60.0,
    },
    {
      cpu: "4",
      memory: "16 GB",
      storage: "80 GB",
      bandwidth: "10 TB",
      monthlyPrice: 120.0,
    },
    {
      cpu: "8",
      memory: "32 GB",
      storage: "160 GB",
      bandwidth: "10 TB",
      monthlyPrice: 240.0,
    },
    {
      cpu: "16",
      memory: "64 GB",
      storage: "320 GB",
      bandwidth: "10 TB",
      monthlyPrice: 480.0,
    },
    {
      cpu: "24",
      memory: "96 GB",
      storage: "480 GB",
      bandwidth: "10 TB",
      monthlyPrice: 720.0,
    },
    {
      cpu: "32",
      memory: "128 GB",
      storage: "640 GB",
      bandwidth: "10 TB",
      monthlyPrice: 960.0,
    },
    {
      cpu: "40",
      memory: "160 GB",
      storage: "768 GB",
      bandwidth: "10 TB",
      monthlyPrice: 1200.0,
    },
    {
      cpu: "64",
      memory: "192 GB",
      storage: "960 GB",
      bandwidth: "10 TB",
      monthlyPrice: 1920.0,
    },
    {
      cpu: "96",
      memory: "256 GB",
      storage: "1280 GB",
      bandwidth: "10 TB",
      monthlyPrice: 3840.0,
    },
  ],
  footer: {
    text1: (
      <>
        Note : AMD is Gen3 and Gen4.
        <br />
        <Balancer>
          We offer a wide range of Cloud VM configurations to suit your workloads. The
          prices listed are indicative and may fluctuate based on resource availability
          and billing cycles. Reach out to our{" "}
          <Link
            href="#contact-us"
            onClick={(e) => {
              e.preventDefault();
              scrollToElement("contact-us", 120);
            }}
            target="_blank"
            style={{ textDecoration: "underline", color: "#1E87F0", fontWeight: 700 }}
          >
            sales team
          </Link>{" "}
          for customized plans or volume monthlyPrice.
        </Balancer>
      </>
    ),
    btnText: "Get a Custom Quote.",
  },
};

export const CLOUD_VM_SHARED_INTEL: CloudVmDataType = {
  heading: "Cloud VM - Shared (Older Gen Intel Based)",
  data: [
    {
      cpu: "1",
      memory: "0.5 GB",
      storage: "10 GB",
      bandwidth: "1 TB",
      monthlyPrice: 2.5,
    },
    {
      cpu: "1",
      memory: "1 GB",
      storage: "25 GB",
      bandwidth: "1 TB",
      monthlyPrice: 5.0,
    },
    {
      cpu: "2",
      memory: "2 GB",
      storage: "50 GB",
      bandwidth: "1 TB",
      monthlyPrice: 9.0,
    },
    {
      cpu: "2",
      memory: "3 GB",
      storage: "60 GB",
      bandwidth: "1 TB",
      monthlyPrice: 15.0,
    },
    {
      cpu: "2",
      memory: "4 GB",
      storage: "80 GB",
      bandwidth: "1 TB",
      monthlyPrice: 20.0,
    },
    {
      cpu: "4",
      memory: "8 GB",
      storage: "160 GB",
      bandwidth: "1 TB",
      monthlyPrice: 40.0,
    },
    {
      cpu: "6",
      memory: "16 GB",
      storage: "320 GB",
      bandwidth: "1 TB",
      monthlyPrice: 80.0,
    },
    {
      cpu: "8",
      memory: "32 GB",
      storage: "640 GB",
      bandwidth: "5 TB",
      monthlyPrice: 160.0,
    },
    {
      cpu: "16",
      memory: "64 GB",
      storage: "1280 GB",
      bandwidth: "5 TB",
      monthlyPrice: 320.0,
    },
    {
      cpu: "24",
      memory: "96 GB",
      storage: "1600 GB",
      bandwidth: "5 TB",
      monthlyPrice: 640.0,
    },
  ],
  footer: {
    text1: (
      <>
        Note : Intel Compute is based on Broadwell, haswell Architecture.
        <br />
        <Balancer>
          We offer a wide range of Cloud VM configurations to suit your workloads. The
          prices listed are indicative and may fluctuate based on resource availability
          and billing cycles. Reach out to our{" "}
          <Link
            href="#contact-us"
            onClick={(e) => {
              e.preventDefault();
              scrollToElement("contact-us", 120);
            }}
            target="_blank"
            style={{ textDecoration: "underline", color: "#1E87F0", fontWeight: 700 }}
          >
            sales team
          </Link>{" "}
          for customized plans or volume monthly price.
        </Balancer>
      </>
    ),
    btnText: "Get a Custom Quote.",
  },
};
